// Copyright (c) Docugami, Inc. All rights reserved.

import { selectChunk } from "hooks/chunkSelection";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { Mode } from "sdv/Mode";
import { IResources } from "sdv/Resources";
import { SelectionFilters } from "sdv/SelectionFilters";
import { Point } from "sdv/Types";
import { BoundsComponent } from "sdv/components/BoundsComponent";
import { ChunkSelectorComponent } from "sdv/components/ChunkSelectorComponent";
import { ControlComponent, ControlType } from "sdv/components/ControlComponent";
import { CursorComponent } from "sdv/components/CursorComponent";
import { SelectionContextComponent } from "sdv/components/SelectionContextComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { ComponentId, tagComponent } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { createSelectedSpanEntity } from "sdv/engine/EntityFactory";
import { ActionId } from "sdv/input/Actions";
import { IInput } from "sdv/input/InputController";
import { ChunkStructure } from "store/dgml/dgmlTypes";
import { CanvasSelectionMode } from "store/documents/types";
import { pointInBox, pointInBoxes } from "store/shared_types";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { ISystem, Queries } from "../engine/Engine";

/**
 * Manages highlighting chunks on hover and selecting them on click. It also keeps
 * the DocumentChunkSelection singleton up to date.
 */
export class ControlSelectionSystem implements ISystem {
    private _selectedEntityName: string = '';
    private _selectionTargetEntity: string | null = null;
    private _pendingActiveRootUpdateForSelection = false;

    get selectedEntityName() { return this._selectedEntityName; }
    set selectedEntityName(value: string) {
        if (value !== this._selectedEntityName) {
            this._selectedEntityName = value;
            if (this.resources.set("selectedEntityName", value)) {
                const control = this.entities.get(this._selectedEntityName)?.getComponent<ControlComponent>(ComponentId.Control);
                const documentId = this.resources.get("documentId").value;
                if (!!control) {
                    const instance = DgmlInstancesSingleton.getInstanceById(documentId, control.id);
                    if (!!instance) {
                        selectChunk(instance.spanId);
                    }
                }
                else {
                    selectChunk();
                }
            }
        }
    }

    get updateEnabled() { return true }

    input!: IInput;
    entities!: IEntityManager;
    resources!: IResources;
    queries: Queries = {
        cursor: { types: [ComponentId.Cursor] },
        hoverControl: { types: [ComponentId.Control, ComponentId.Hover] },
        hitControl: { types: [ComponentId.Control, ComponentId.CursorHit] },
        selected: { types: [ComponentId.TokenRange, ComponentId.Selected] },
        activeSelectors: { types: [ComponentId.ChunkSelector, ComponentId.CursorHit] },
        allSelectors: { types: [ComponentId.ChunkSelector] },
        selectionContexts: { types: [ComponentId.SelectionContext] },
        ephemeralSelections: { types: [ComponentId.EphemeralSelection] },
        activeRoot: { types: [ComponentId.RootControl, ComponentId.Bounds, ComponentId.Active] },
        defaultRoot: { types: [ComponentId.RootControl, ComponentId.Default] },
        allRoots: { types: [ComponentId.RootControl] }
    }

    update() {
        this.handleDeselect();
        this.handleSelectionChanges();
        this.cleanUpEphemeralSelections();

        const mode = this.resources.get("mode").value;
        const tool = this.resources.get("tool").value;
        const selectionFilters = this.resources.get("selectionFilters").value;
        if (mode !== Mode.Selection || tool !== CanvasSelectionMode.Select) {
            return;
        }

        this.ensureActiveRoot();
        this.cleanupSelectors();
        this.cleanupSelectionContexts();
        this.updateHover(selectionFilters);

        this.handleSelectionInput(selectionFilters);
    }

    private handleDeselect() {
        const state = this.input.getButton(ActionId.Deselect);
        if (state.isDown) {
            state.handle();
            this.selectControl(null);
        }
    }

    private ensureActiveRoot() {
        if (!!this.queries.allRoots.query!.any()) {
            if (this._pendingActiveRootUpdateForSelection) {
                const selected = this.queries.selected.query!.single();
                if (!!selected) {
                    this.setRootForEntity(selected)
                }

                this._pendingActiveRootUpdateForSelection = false;
            }
            else {
                const activeRoot = this.queries.activeRoot.query!.single();
                if (!activeRoot) {
                    const defaultRoot = this.queries.defaultRoot.query!.single();
                    defaultRoot?.ensureComponent(tagComponent(ComponentId.Active));
                }
            }
        }
    }

    private cleanupSelectors() {
        // Clean-up selectors for entities that no longer exist
        for (const selector of this.queries.allSelectors.query!.entities) {
            const selectorComp = selector.getComponent<ChunkSelectorComponent>(ComponentId.ChunkSelector)!;
            if (selectorComp.targetEntity.isPendingDelete || !selectorComp.targetEntity.active) {
                selector.destroy();
            }
        }
    }

    private cleanupSelectionContexts() {
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        for (const entity of this.queries.selectionContexts.query!.entities) {
            const contextComp = entity.getComponent<SelectionContextComponent>(ComponentId.SelectionContext)!;
            if (!!contextComp.exitOnCursorLeave && (!cursor || !pointInBox(cursor.x, cursor.y, contextComp.cursorZone))) {
                if (contextComp.exitOnCursorLeave === "destroy") {
                    entity.destroy()
                }
                else {
                    entity.removeComponent(ComponentId.SelectionContext);
                }
            }
        }
    }

    private updateHover(selectionFilters: SelectionFilters | null) {
        let newHoverEntity: Entity | null = null;
        if (selectionFilters !== SelectionFilters.Disabled) {
            const selectorEntity = this.queries.activeSelectors.query!.first()?.getComponent<ChunkSelectorComponent>(ComponentId.ChunkSelector)!.targetEntity ?? null;
            const hitEntity = this.queries.hitControl.query!.entities[0];
            if (!!selectorEntity) {
                newHoverEntity = selectorEntity;
            }
            else if (!!hitEntity) {
                // Resolve the hit test control to the one we actually want to highlight
                newHoverEntity = resolveControlEntityToHover(
                    hitEntity,
                    this.queries.selected.query!.entities[0] ?? null,
                    this.entities);
            }
        }

        newHoverEntity = this.filterControlEntity(newHoverEntity, selectionFilters);
        this.hoverEntity(newHoverEntity);
    }

    private handleSelectionInput(selectionFilters: SelectionFilters | null) {
        if (selectionFilters === SelectionFilters.Disabled) {
            return;
        }

        const currentHover = this.queries.hoverControl.query!.single();

        const selectState = this.input.getButton(ActionId.Select);
        if (selectState.isDown && !selectState.handled) {
            this._selectionTargetEntity = currentHover?.name ?? null;
            if (!!this._selectedEntityName) {
                selectState.handle()
            }
        }
        else if (selectState.isUp) {
            let targetSelect: Entity | null = null;
            if (!selectState.handled && !!this._selectionTargetEntity && this._selectionTargetEntity === currentHover?.name) {
                selectState.handle();
                targetSelect = currentHover;
            }

            this._selectionTargetEntity = null;
            this.selectControl(targetSelect, this.queries.cursor.query!.single()?.getComponent<CursorComponent>(ComponentId.Cursor)!.point);
        }
        else if (!selectState.isPressed) {
            this._selectionTargetEntity = null;
        }
    }

    private handleSelectionChanges() {
        const selectedEntityNameResource = this.resources.get("selectedEntityName");
        if (selectedEntityNameResource.updated && selectedEntityNameResource.value !== this.selectedEntityName) {
            // Selected entity was updated externally, we should reflect the change here and focus the selection
            this.selectedEntityName = selectedEntityNameResource.value;
            if (!this.selectedEntityName) {
                this.selectControl(null);
            }
        }

        const currentSelected = this.queries.selected.query!.entities[0];
        if (!!this.selectedEntityName && (!currentSelected || currentSelected.name !== this.selectedEntityName)) {
            let selected = this.entities.get(this.selectedEntityName);

            if (!selected && !this.resources.check("dgml")) {
                const documentId = this.resources.get("documentId").value;
                const spanId = SpanIdUtils.tryParse(this.selectedEntityName);
                if (!!spanId) {
                    selected = createSelectedSpanEntity(this.entities, documentId, spanId);
                }
            }

            if (!!selected) {
                this.selectControl(selected);
            }
        }
    }

    private cleanUpEphemeralSelections() {
        for (const entity of this.queries.ephemeralSelections.query!.entities) {
            if (entity.hasComponent(ComponentId.Control)) {
                // This chunk was found in the DGML so we can remove the ephemeral selection tracking
                entity.removeComponent(ComponentId.EphemeralSelection);
            }
            else if (!entity.hasComponent(ComponentId.Selected) && !this.resources.check("dgml")) {
                // The ephemeral entity is no longer selected or the DGML changed and this entity is no longer orphaned
                this.entities.destroy(entity);
            }
        }
    }

    private selectControl(entity: Entity | null, cursor?: Point) {
        if (entity?.hasComponent(ComponentId.Outflow)) {
            // We don't select outflows direclty - we select the root control child of them
            entity = this.entities.get(entity.getComponent<ControlComponent>(ComponentId.Control)?.node.children[0] ?? '');
        }

        const currentSelected = this.queries.selected.query!.entities[0] ?? null;
        const currentActiveRoot = this.queries.activeRoot.query!.single();
        if (currentSelected === entity) {
            if (!currentSelected && !!currentActiveRoot) {
                // Handling deselects when nothing is selected:
                // 1. User is clicking on empty space w/in the active root => leave the active root unchanged
                // 2. User is clicking outside the active root => go up one outflow level
                // 3. Deselect was triggered programmatically which should exit outflows one level at a time
                const bounds = currentActiveRoot.getComponent<BoundsComponent>(ComponentId.Bounds)!;
                if (!cursor || !pointInBoxes(cursor.x, cursor.y, bounds.containerBoxes)) {
                    let current = currentActiveRoot.getComponent<ControlComponent>(ComponentId.Control)!.node;
                    let parentChunk = this.entities.get(current.parent ?? '')?.getComponent<ControlComponent>(ComponentId.Control)!.node;
                    while (!!parentChunk && parentChunk.structure?.type !== ChunkStructure.OutflowContainer) {
                        current = parentChunk;
                        parentChunk = this.entities.get(current.parent ?? '')?.getComponent<ControlComponent>(ComponentId.Control)!.node
                    }

                    currentActiveRoot?.removeComponent(ComponentId.Active);
                    if (!!current) {
                        const targetRootEntity = this.entities.get(current.spanId);
                        if (targetRootEntity?.hasComponent(ComponentId.RootControl)) {
                            targetRootEntity.ensureComponent(tagComponent(ComponentId.Active));
                        }
                    }
                }
            }

            return;
        }

        currentSelected?.removeComponent(ComponentId.Selected);
        entity?.addComponent(tagComponent(ComponentId.Selected));
        this.selectedEntityName = entity?.name ?? '';

        if (!!entity) {
            // Check to see if the active root needs to change
            if (!this.queries.allRoots.query!.any()) {
                this._pendingActiveRootUpdateForSelection = true;
            }
            else {
                this.setRootForEntity(entity)
            }
        }
    }

    private setRootForEntity(entity: Entity) {
        const currentActiveRoot = this.queries.activeRoot.query!.single();
        const tokenRange = entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
        const newActiveRoot = this.queries.allRoots.query!.entities.find(x => TokenRangeUtils.rangeIsContained(tokenRange, x.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange));
        if (newActiveRoot !== currentActiveRoot) {
            currentActiveRoot?.removeComponent(ComponentId.Active);
            newActiveRoot?.ensureComponent(tagComponent(ComponentId.Active))
        }
    }

    private hoverEntity(entity: Entity | null) {
        const currentHover = this.queries.hoverControl.query!.entities[0];
        if (currentHover === entity) {
            return;
        }

        currentHover?.removeComponent(ComponentId.Hover);
        entity?.addComponent(tagComponent(ComponentId.Hover));
    }

    private filterControlEntity(entity: Entity | null, selectionFilters: SelectionFilters | null) {
        if (!!entity && entity.hasComponent(ComponentId.Control)) {
            const controlComp = entity.getComponent<ControlComponent>(ComponentId.Control)!;
            for (const entity of this.queries.selectionContexts.query!.entities) {
                const contextComp = entity.getComponent<SelectionContextComponent>(ComponentId.SelectionContext)!;
                if (!TokenRangeUtils.rangeIsContained({ fromTok: controlComp.fromTok, toTok: controlComp.toTok }, contextComp.tokenRange)) {
                    return null;
                }
            }

            if (!!selectionFilters) {
                // Filter exists and is not "SelectionFilters.Disabled"
                let controlFilters = SelectionFilters.Disabled;
                if (entity.hasComponent(ComponentId.SimpleTableColumnHeader)) {
                    controlFilters |= SelectionFilters.SimpleColumns;
                }

                if (controlComp.type === ControlType.Table) {
                    controlFilters |= SelectionFilters.Tables;
                }

                if (!(selectionFilters & controlFilters)) {
                    return null;
                }
            }
        }

        return entity;
    }
}

interface SelectionHierarchyInfo {
    table?: Entity;
    tableCell?: Entity;
    simpleTable?: Entity;
    simpleTableColumnHeader?: Entity;
    listItemMarker?: Entity;
    header?: Entity;
    firstHyperline?: Entity;
    largestSmallChunk?: Entity
    outflowContainer?: Entity;
}

function resolveControlEntityToHover(entity: Entity, selectedEntity: Entity | null, entities: IEntityManager): Entity | null {
    if (entity.name === selectedEntity?.name) {
        return entity;
    }

    const info = getSelectionHierarchyInfo(entity, selectedEntity, entities);

    let target: Entity | null = info.largestSmallChunk ?? info.firstHyperline ?? null;
    if (!!info.header || !!info.listItemMarker) {
        target = (info.header || info.listItemMarker)!;

        const controlComp = target.getComponent<ControlComponent>(ComponentId.Control)!;
        const parent = entities.get(controlComp.parent);
        if (!!parent &&
            controlComp.parent !== selectedEntity?.name &&
            controlComp.parent !== info.tableCell?.name &&
            !parent.getComponent<ControlComponent>(ComponentId.Control)?.isRoot) {
            target = parent
        }
    }

    if (!!info.outflowContainer) {
        target = info.outflowContainer;
    }
    else if (!!info.tableCell) {
        if (!!target && !!selectedEntity) {
            // If we currently have a child of a table cell selected, then allow selecting other children directly without having to re-select the cell
            const tableTokenRange = info.tableCell.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            const selectedTokenRange = selectedEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            const targetTokenRange = target.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            if (!TokenRangeUtils.rangeIsContained(selectedTokenRange, tableTokenRange) || !TokenRangeUtils.rangeIsContained(targetTokenRange, tableTokenRange)) {
                target = info.tableCell;
            }
        }
        else {
            target = info.tableCell
        }
    }

    return target
}

function getSelectionHierarchyInfo(entity: Entity, selectedEntity: Entity | null, entities: IEntityManager): SelectionHierarchyInfo {
    // Case 1: We are inside a heading, select our the parent chunk (container | list_item) of the heading
    // Case 2: We are inside of a list marker, select the parent chunk (list_item)
    // Case 3: Highest non-structural ancestor prior to a structural one
    // Case 4: We are inside a table and should select the cell by default
    let iter: Entity | null = entity;
    const result: SelectionHierarchyInfo = {}

    while (!!iter) {
        if (iter.name === selectedEntity?.name) {
            break;
        }

        const control: ControlComponent = iter.getComponent<ControlComponent>(ComponentId.Control)!;
        if (control.node.structure?.type === ChunkStructure.ListItemMarker) {
            result.listItemMarker = iter;
        }
        else if (control.node.structure?.type === ChunkStructure.Heading1) {
            result.header = iter;
        }
        else if (control.type === ControlType.SmallChunk) {
            result.largestSmallChunk = iter;
        }
        else if (control.type === ControlType.Outflow && !result.outflowContainer) {
            result.outflowContainer = iter;
        }
        else if (control.type === ControlType.LargeChunk && !result.firstHyperline) {
            result.firstHyperline = iter;
        }
        else if (control.type === ControlType.TableCell) {
            result.tableCell = iter;

            if (iter.hasComponent(ComponentId.SimpleTableColumnHeader)) {
                result.simpleTableColumnHeader = iter;
            }
        }
        else if (control.type === ControlType.Table) {
            result.table = iter;

            if (iter.hasComponent(ComponentId.SimpleTable)) {
                result.simpleTable = iter;
            }

            break;
        }

        iter = entities.get(control.parent)!;
    }

    return result;
}


